import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Languages, Menu, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { label: "Solutions", href: "/#services" },
  { label: "Industries", href: "/#industries" },
  { label: "Languages", href: "/#languages" },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "About Us", href: "/#why-us" },
  { label: "Contact", href: "/contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goQuote = () => {
    setOpen(false);
    if (window.location.pathname !== "/") {
      navigate("/#quote");
    } else {
      document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-slate-200 shadow-[0_8px_30px_rgba(11,17,30,0.06)]"
          : "backdrop-blur-md bg-white/60 border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5 group">
          <span className="w-9 h-9 rounded-lg bg-[#0B111E] text-white flex items-center justify-center transition-colors duration-300 group-hover:bg-[#C87A4B]">
            <Languages className="w-5 h-5" strokeWidth={1.8} />
          </span>
          <span className="font-heading font-bold text-xl tracking-tight text-[#0B111E]">
            Translate<span className="text-[#C87A4B]">It</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="header-nav">
          {NAV.map((item) => (
            <a
              key={item.label}
              href={item.href}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-slate-600 hover:text-[#0B111E] transition-colors duration-200"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={goQuote}
            data-testid="header-get-quote-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-[#C87A4B] hover:bg-[#B3673A] text-white text-sm font-semibold px-5 py-2.5 rounded-lg shadow-md transition-all duration-200 hover:-translate-y-0.5"
          >
            Get a Quote
            <ArrowRight className="w-4 h-4" />
          </button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-btn"
                className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-slate-200 bg-white"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5 text-[#0B111E]" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-white">
              <div className="flex flex-col gap-6 mt-10">
                {NAV.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setOpen(false)}
                    className="font-heading text-2xl font-semibold text-[#0B111E] hover:text-[#C87A4B] transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
                <button
                  onClick={goQuote}
                  data-testid="mobile-get-quote-btn"
                  className="mt-4 inline-flex items-center justify-center gap-2 bg-[#C87A4B] text-white font-semibold px-5 py-3.5 rounded-lg"
                >
                  Get a Quote <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
